(() => {
  const gallerySelector = 'media-gallery';
  const debugStorageKey = 'mocGalleryArrowDebug';
  const thumbnailSyncGuardTimeout = 1500;
  let debugClickId = 0;

  const isDebugEnabled = () => {
    try {
      return window.localStorage.getItem(debugStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  };

  // A slide whose photo belongs to another variant stays in the DOM and is
  // hidden with display none. Stepping onto one reads as a dead arrow press:
  // the index moves but the screen does not change. So the arrows walk the
  // visible slides only. Visibility is read fresh on every call, because
  // choosing a variant changes which slides are hidden.
  const isHiddenSlide = (slide) => window.getComputedStyle(slide).display === 'none';

  const getControls = (gallery) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]:not(button)');

    if (!viewer || !thumbnails) return null;

    const buttons = Array.from(thumbnails.querySelectorAll('button.slider-button'));
    const slides = Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]')).filter(
      (slide) => !isHiddenSlide(slide)
    );

    if (buttons.length === 0 || slides.length === 0) return null;

    return { buttons, slides, viewer, thumbnails };
  };

  const getDebugState = (gallery) => {
    const controls = getControls(gallery);

    if (!controls) {
      return { galleryId: gallery?.id || null, controlsAvailable: false };
    }

    const { slides, viewer, thumbnails } = controls;
    const activeSlide = slides.find((slide) => slide.classList.contains('is-active'));
    const activeThumbnailButton = thumbnails.querySelector('button[aria-current="true"]');
    const activeThumbnail = activeThumbnailButton?.closest('[data-target]');
    const viewerSlider = viewer.slider || viewer.querySelector('[id^="Slider-Gallery"]');
    const thumbnailSlider = thumbnails.slider || thumbnails.querySelector('[id^="Slider-Thumbnails"]');

    return {
      galleryId: gallery.id || null,
      controlsAvailable: true,
      activeMediaId: activeSlide?.dataset.mediaId || null,
      activeThumbnailTarget: activeThumbnail?.dataset.target || null,
      viewerScrollLeft: viewerSlider?.scrollLeft || 0,
      thumbnailScrollLeft: thumbnailSlider?.scrollLeft || 0,
      previousDisabled: controls.buttons.find((button) => button.name === 'previous')?.disabled || false,
      nextDisabled: controls.buttons.find((button) => button.name === 'next')?.disabled || false,
      time: Math.round(window.performance.now())
    };
  };

  const debugState = (gallery, eventName, details = {}) => {
    if (!isDebugEnabled()) return;

    console.log('[MOC gallery arrows]', eventName, {
      ...details,
      ...getDebugState(gallery)
    });
  };

  const scheduleDebugCheckpoints = (gallery, clickId) => {
    if (!isDebugEnabled()) return;

    [0, 50, 150, 350, 600, 900].forEach((delay) => {
      window.setTimeout(() => {
        debugState(gallery, 'click:checkpoint', { clickId, delay });
      }, delay);
    });
  };

  const setDisabled = (button, disabled) => {
    if (disabled && !button.hasAttribute('disabled')) {
      button.setAttribute('disabled', 'disabled');
    } else if (!disabled && button.hasAttribute('disabled')) {
      button.removeAttribute('disabled');
    }
  };

  const disableThumbnailSliderArrows = ({ buttons, thumbnails }) => {
    if (typeof thumbnails.onButtonClickHandler !== 'function') return;

    buttons.forEach((button) => {
      button.removeEventListener('click', thumbnails.onButtonClickHandler);
    });
  };

  const clearThumbnailSyncGuard = (gallery, mediaId) => {
    if (mediaId && gallery.mocGalleryArrowTargetMediaId !== mediaId) return;

    gallery.mocGalleryArrowTargetMediaId = null;

    if (gallery.mocGalleryArrowGuardTimeout) {
      window.clearTimeout(gallery.mocGalleryArrowGuardTimeout);
      gallery.mocGalleryArrowGuardTimeout = null;
    }
  };

  const setThumbnailSyncGuard = (gallery, mediaId) => {
    clearThumbnailSyncGuard(gallery);
    gallery.mocGalleryArrowTargetMediaId = mediaId;
    gallery.mocGalleryArrowGuardTimeout = window.setTimeout(() => {
      clearThumbnailSyncGuard(gallery, mediaId);
    }, thumbnailSyncGuardTimeout);
  };

  const guardThumbnailSync = (gallery) => {
    if (gallery.mocGalleryOriginalSetActiveThumbnail || typeof gallery.setActiveThumbnail !== 'function') return;

    const originalSetActiveThumbnail = gallery.setActiveThumbnail;

    gallery.mocGalleryOriginalSetActiveThumbnail = originalSetActiveThumbnail;
    gallery.setActiveThumbnail = function (thumbnail) {
      const expectedMediaId = this.mocGalleryArrowTargetMediaId;
      const thumbnailMediaId = thumbnail?.dataset.target;

      if (expectedMediaId && thumbnailMediaId && thumbnailMediaId !== expectedMediaId) {
        debugState(this, 'thumbnail:stale-update-blocked', {
          expectedMediaId,
          attemptedMediaId: thumbnailMediaId
        });
        return;
      }

      return originalSetActiveThumbnail.call(this, thumbnail);
    };
  };

  // The viewer list is overflow hidden with scroll snap and smooth scrolling, so
  // the scrollTo the theme runs inside setActiveMedia never lands: the snap
  // cancels it and the big photo stays on the first slide however the visitor
  // switches, by arrow or by clicking a thumbnail. Suspending smooth scrolling
  // for the jump is what actually moves it, and it has to run after the theme
  // has had its own go rather than race it.
  const bringActiveIntoView = (gallery) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const active = viewer && viewer.querySelector('.product__media-item.is-active');
    const track = active && active.parentElement;

    if (!track || isHiddenSlide(active)) return;

    // offsetLeft is measured from the border box, so it carries the one pixel
    // border the viewer draws. Scrolling to it lands a pixel past the snap
    // point, and the browser then animates that pixel back, which is what reads
    // as a twitch right after the photo changes.
    const target = active.offsetLeft - track.clientLeft;

    if (Math.abs(track.scrollLeft - target) < 1) return;

    track.scrollLeft = target;
  };

  // The section sets scroll-behavior smooth on the viewer list. Every scroll of
  // it is therefore animated, including the theme's own attempt inside
  // setActiveMedia, which aims a pixel off and is left mid flight when we move
  // the strip ourselves. Two animations fighting over the same element is what
  // the client sees as a stutter. An inline value outranks the section rule, so
  // the switch becomes a clean cut and nothing is left animating.
  const settleScrolling = (gallery) => {
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const track = viewer && viewer.querySelector('[id^="Slider-Gallery"]');

    if (track) track.style.scrollBehavior = 'auto';
  };

  // Immediately, so the photo answers the click instead of waiting a quarter of
  // a second, and once more afterwards in case the theme moves the strip late.
  const scheduleBringIntoView = (gallery) => {
    bringActiveIntoView(gallery);
    window.clearTimeout(gallery.mocGalleryViewTimeout);
    gallery.mocGalleryViewTimeout = window.setTimeout(() => bringActiveIntoView(gallery), 250);
  };

  const updateControls = (gallery) => {
    const controls = getControls(gallery);

    if (!controls) return;

    const { buttons, slides, viewer } = controls;
    const activeIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);

    buttons.forEach((button) => {
      button.setAttribute('aria-controls', viewer.id);
      if (button.name === 'previous') {
        setDisabled(button, activeIndex === 0);
      } else if (button.name === 'next') {
        setDisabled(button, activeIndex === slides.length - 1);
      }
    });
  };

  const cancelScheduledUpdate = (gallery) => {
    if (gallery.mocGalleryArrowFrame) {
      window.cancelAnimationFrame(gallery.mocGalleryArrowFrame);
      gallery.mocGalleryArrowFrame = null;
    }

    if (gallery.mocGalleryArrowFallback) {
      window.clearTimeout(gallery.mocGalleryArrowFallback);
      gallery.mocGalleryArrowFallback = null;
    }
  };

  // Update now, and make sure nothing is left pending. Used wherever the arrow
  // state has to be right immediately, such as straight after a click.
  const runUpdate = (gallery) => {
    cancelScheduledUpdate(gallery);
    updateControls(gallery);
  };

  const scheduleUpdate = (gallery) => {
    // A frame never arrives while the tab is in the background, and iOS Safari
    // suspends frames whenever the page is not in front. The old code set a
    // frame id, waited for a callback that never came, and then skipped every
    // later update because the id was still set. That left the arrows showing
    // the state of the photo the visitor had already moved away from.
    if (document.hidden) {
      runUpdate(gallery);
      return;
    }

    if (gallery.mocGalleryArrowFrame) return;

    gallery.mocGalleryArrowFrame = window.requestAnimationFrame(() => {
      gallery.mocGalleryArrowFrame = null;
      updateControls(gallery);
    });

    // Belt and braces: if the frame is dropped, repair the state anyway.
    window.clearTimeout(gallery.mocGalleryArrowFallback);
    gallery.mocGalleryArrowFallback = window.setTimeout(() => {
      runUpdate(gallery);
    }, 250);
  };

  const initialiseGallery = (gallery) => {
    if (!gallery) return;

    const controls = getControls(gallery);

    if (!controls) return;

    disableThumbnailSliderArrows(controls);
    guardThumbnailSync(gallery);

    if (gallery.dataset.mocGalleryArrowControls === 'true') {
      updateControls(gallery);
      return;
    }

    settleScrolling(gallery);
    gallery.dataset.mocGalleryArrowControls = 'true';
    updateControls(gallery);

    controls.viewer.addEventListener('slideChanged', (event) => {
      const eventMediaId = event.detail?.currentElement?.dataset.mediaId || null;

      debugState(gallery, 'viewer:slideChanged', {
        eventMediaId
      });

      if (eventMediaId === gallery.mocGalleryArrowTargetMediaId) {
        clearThumbnailSyncGuard(gallery, eventMediaId);
      }
    });
    controls.thumbnails.addEventListener('slideChanged', (event) => {
      debugState(gallery, 'thumbnails:slideChanged', {
        eventThumbnailTarget: event.detail?.currentElement?.dataset.target || null
      });
    });

    const observer = new MutationObserver((mutations) => {
      scheduleUpdate(gallery);
      scheduleBringIntoView(gallery);

      if (!isDebugEnabled()) return;

      debugState(gallery, 'mutation', {
        changes: mutations.map((mutation) => ({
          attribute: mutation.attributeName,
          targetId: mutation.target.id || null
        }))
      });
    });
    observer.observe(controls.viewer, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    observer.observe(controls.thumbnails, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current', 'disabled']
    });
  };

  const handleThumbnailArrow = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest(`${gallerySelector} [id^="GalleryThumbnails"]:not(button) button.slider-button`);

    if (!button) return;

    const gallery = button.closest(gallerySelector);

    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    initialiseGallery(gallery);

    const controls = getControls(gallery);

    if (!controls || !controls.buttons.includes(button)) return;

    if (button.name !== 'previous' && button.name !== 'next') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const { slides } = controls;
    const activeIndex = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
    const nextIndex = button.name === 'next' ? activeIndex + 1 : activeIndex - 1;
    const clickId = ++debugClickId;

    debugState(gallery, 'click:start', {
      clickId,
      direction: button.name,
      activeIndex,
      nextIndex,
      targetMediaId: slides[nextIndex]?.dataset.mediaId || null
    });

    if (nextIndex >= 0 && nextIndex < slides.length) {
      const targetMediaId = slides[nextIndex].dataset.mediaId;

      setThumbnailSyncGuard(gallery, targetMediaId);
      gallery.setActiveMedia(targetMediaId, false);
    }

    scheduleBringIntoView(gallery);
    debugState(gallery, 'click:after-set-active-media', { clickId });
    scheduleDebugCheckpoints(gallery, clickId);
    runUpdate(gallery);
  };

  document.addEventListener('click', handleThumbnailArrow, true);

  const refreshInitialisedGalleries = () => {
    document.querySelectorAll(gallerySelector).forEach((gallery) => {
      if (gallery.dataset.mocGalleryArrowControls !== 'true') return;
      runUpdate(gallery);
    });
  };

  // Coming back to the tab, or back through the browser history, repairs any
  // arrow state that went stale while frames were suspended.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    refreshInitialisedGalleries();
  });
  window.addEventListener('pageshow', refreshInitialisedGalleries);

  const initialiseExistingGalleries = () => {
    document.querySelectorAll(gallerySelector).forEach(initialiseGallery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseExistingGalleries, { once: true });
  } else {
    initialiseExistingGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll(gallerySelector).forEach(initialiseGallery);
  });
})();
